import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getCostToServe } from "@/lib/cost-to-serve.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/operator-shell";
import { ScopeBar } from "@/components/scope-bar";
import { HelpButton } from "@/components/kb/help-button";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { KpiCard } from "@/components/reporting/kpi-card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusPill } from "@/components/ui/status-pill";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ArrowLeft, ArrowUpDown, TrendingDown, TrendingUp, DollarSign, Percent, Users } from "lucide-react";
import { chartColors, axisProps, gridProps, tooltipStyle } from "@/components/reporting/chart-theme";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Legend } from "recharts";

export const Route = createFileRoute("/_authenticated/app/billing/profitability")({ component: ProfitabilityPage });

type Row = {
  period_month: string; customer_id: string; customer_name: string;
  revenue_cents: number; label_cost_cents: number; consumable_cost_cents: number;
  labor_cost_cents: number; total_cost_cents: number; margin_cents: number; margin_pct: number | null;
};

type SortKey = "margin_pct" | "margin_cents" | "revenue_cents" | "total_cost_cents";

const fmt$ = (cents: number) => `$${(Math.round(cents) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

function marginTone(pct: number | null): "neutral" | "warning" | "danger" | "success" {
  if (pct == null) return "neutral";
  if (pct < 0) return "danger";
  if (pct < 10) return "warning";
  return "success";
}

function ProfitabilityPage() {
  const { tenantId } = useScope();
  const fetchCts = useServerFn(getCostToServe);
  const [months, setMonths] = useState(3);
  const [sortKey, setSortKey] = useState<SortKey>("margin_pct");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [activeCustomer, setActiveCustomer] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["profitability", tenantId, months],
    enabled: !!tenantId,
    queryFn: () => fetchCts({ data: { tenantId: tenantId!, months } }),
  });

  const rows = (data?.rows ?? []) as Row[];

  // Aggregate per customer across the period
  const customers = useMemo(() => {
    const m = new Map<string, {
      customer_id: string; customer_name: string;
      revenue_cents: number; cost_cents: number; label: number; consumable: number; labor: number;
      months: number; latestMonth: string;
    }>();
    for (const r of rows) {
      const cur = m.get(r.customer_id) ?? {
        customer_id: r.customer_id, customer_name: r.customer_name,
        revenue_cents: 0, cost_cents: 0, label: 0, consumable: 0, labor: 0, months: 0, latestMonth: r.period_month,
      };
      cur.revenue_cents += Number(r.revenue_cents || 0);
      cur.cost_cents += Number(r.total_cost_cents || 0);
      cur.label += Number(r.label_cost_cents || 0);
      cur.consumable += Number(r.consumable_cost_cents || 0);
      cur.labor += Number(r.labor_cost_cents || 0);
      cur.months += 1;
      if (r.period_month > cur.latestMonth) cur.latestMonth = r.period_month;
      m.set(r.customer_id, cur);
    }
    return Array.from(m.values()).map((c) => {
      const margin = c.revenue_cents - c.cost_cents;
      const pct = c.revenue_cents > 0 ? (margin / c.revenue_cents) * 100 : null;
      return { ...c, margin_cents: margin, margin_pct: pct, total_cost_cents: c.cost_cents };
    });
  }, [rows]);

  const sorted = useMemo(() => {
    const arr = [...customers];
    arr.sort((a, b) => {
      const av = (a as any)[sortKey] ?? 0;
      const bv = (b as any)[sortKey] ?? 0;
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return arr;
  }, [customers, sortKey, sortDir]);

  const totals = useMemo(() => {
    const t = customers.reduce(
      (s, c) => ({
        revenue: s.revenue + c.revenue_cents,
        cost: s.cost + c.cost_cents,
        margin: s.margin + c.margin_cents,
        negCount: s.negCount + (c.margin_cents < 0 ? 1 : 0),
        thinCount: s.thinCount + (c.margin_pct != null && c.margin_pct >= 0 && c.margin_pct < 10 ? 1 : 0),
      }),
      { revenue: 0, cost: 0, margin: 0, negCount: 0, thinCount: 0 },
    );
    const pct = t.revenue > 0 ? (t.margin / t.revenue) * 100 : 0;
    return { ...t, pct };
  }, [customers]);

  const selected = activeCustomer ? customers.find((c) => c.customer_id === activeCustomer) ?? null : null;
  const selectedTrend = useMemo(() => {
    if (!selected) return [];
    return rows
      .filter((r) => r.customer_id === selected.customer_id)
      .sort((a, b) => a.period_month.localeCompare(b.period_month))
      .map((r) => ({
        month: r.period_month.slice(0, 7),
        revenue: r.revenue_cents / 100,
        cost: r.total_cost_cents / 100,
        margin: (r.revenue_cents - r.total_cost_cents) / 100,
      }));
  }, [rows, selected]);

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir(k === "margin_pct" || k === "margin_cents" ? "asc" : "desc"); }
  };

  return (
    <>
      <PageHeader
        title="Customer Profitability"
        description="Revenue vs cost-to-serve per customer. Negative and thin margins flagged for review."
        actions={
          <div className="flex gap-2">
            <ScopeBar />
            <HelpButton moduleKey="billing" />
            <Button variant="outline" size="sm" asChild><Link to="/app/billing"><ArrowLeft className="h-4 w-4 mr-2" />Invoices</Link></Button>
          </div>
        }
      />
      <div className="p-4 sm:p-6 space-y-5">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Lookback</label>
            <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
              <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Last month</SelectItem>
                <SelectItem value="3">Last 3 months</SelectItem>
                <SelectItem value="6">Last 6 months</SelectItem>
                <SelectItem value="12">Last 12 months</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-5">
          <KpiCard label="Revenue" value={fmt$(totals.revenue)} icon={<DollarSign className="h-4 w-4" />} />
          <KpiCard label="Cost to serve" value={fmt$(totals.cost)} icon={<DollarSign className="h-4 w-4" />} />
          <KpiCard label="Gross margin" value={fmt$(totals.margin)} icon={totals.margin >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />} />
          <KpiCard label="Margin %" value={`${totals.pct.toFixed(1)}%`} icon={<Percent className="h-4 w-4" />} />
          <KpiCard label="At-risk customers" value={`${totals.negCount} neg · ${totals.thinCount} thin`} icon={<Users className="h-4 w-4" />} />
        </div>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Customers</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort("revenue_cents")}>
                      <span className="inline-flex items-center gap-1">Revenue <ArrowUpDown className="h-3 w-3 opacity-60" /></span>
                    </TableHead>
                    <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort("total_cost_cents")}>
                      <span className="inline-flex items-center gap-1">Cost <ArrowUpDown className="h-3 w-3 opacity-60" /></span>
                    </TableHead>
                    <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort("margin_cents")}>
                      <span className="inline-flex items-center gap-1">Margin $ <ArrowUpDown className="h-3 w-3 opacity-60" /></span>
                    </TableHead>
                    <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort("margin_pct")}>
                      <span className="inline-flex items-center gap-1">Margin % <ArrowUpDown className="h-3 w-3 opacity-60" /></span>
                    </TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
                  )}
                  {!isLoading && sorted.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No cost-to-serve runs in the selected window. Generate one from Reports → Billing.</TableCell></TableRow>
                  )}
                  {sorted.map((c) => {
                    const tone = marginTone(c.margin_pct);
                    const label = c.margin_pct == null
                      ? "no revenue"
                      : c.margin_pct < 0 ? "margin negative"
                      : c.margin_pct < 10 ? "margin thin"
                      : "healthy";
                    return (
                      <TableRow key={c.customer_id} className="cursor-pointer hover:bg-muted/40" onClick={() => setActiveCustomer(c.customer_id)}>
                        <TableCell className="font-medium">{c.customer_name}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmt$(c.revenue_cents)}</TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">{fmt$(c.cost_cents)}</TableCell>
                        <TableCell className={`text-right tabular-nums font-medium ${c.margin_cents < 0 ? "text-destructive" : ""}`}>{fmt$(c.margin_cents)}</TableCell>
                        <TableCell className="text-right tabular-nums">{c.margin_pct == null ? "—" : `${c.margin_pct.toFixed(1)}%`}</TableCell>
                        <TableCell><StatusPill tone={tone}>{label}</StatusPill></TableCell>
                        <TableCell className="text-right">
                          <Link
                            to="/app/billing/customers/$customerId"
                            params={{ customerId: c.customer_id }}
                            className="text-xs text-primary hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Rate card →
                          </Link>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      <Sheet open={!!activeCustomer} onOpenChange={(o) => !o && setActiveCustomer(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader><SheetTitle>{selected?.customer_name ?? ""}</SheetTitle></SheetHeader>
          {selected && (
            <div className="mt-4 space-y-5">
              <div className="grid grid-cols-3 gap-3">
                <div className="border rounded-md p-3"><div className="text-[11px] text-muted-foreground uppercase">Revenue</div><div className="text-lg font-semibold tabular-nums">{fmt$(selected.revenue_cents)}</div></div>
                <div className="border rounded-md p-3"><div className="text-[11px] text-muted-foreground uppercase">Cost</div><div className="text-lg font-semibold tabular-nums">{fmt$(selected.cost_cents)}</div></div>
                <div className="border rounded-md p-3"><div className="text-[11px] text-muted-foreground uppercase">Margin</div><div className={`text-lg font-semibold tabular-nums ${selected.margin_cents < 0 ? "text-destructive" : ""}`}>{fmt$(selected.margin_cents)}</div></div>
              </div>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Monthly Trend</CardTitle></CardHeader>
                <CardContent className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={selectedTrend} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
                      <CartesianGrid {...gridProps} />
                      <XAxis dataKey="month" {...axisProps} />
                      <YAxis {...axisProps} width={42} />
                      <Tooltip {...tooltipStyle} />
                      <Legend />
                      <Line type="monotone" dataKey="revenue" stroke={chartColors.success} strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="cost" stroke={chartColors.warning ?? chartColors.primary} strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="margin" stroke={chartColors.primary} strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Cost Breakdown</CardTitle></CardHeader>
                <CardContent className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={[{ k: "Labor", v: selected.labor / 100 }, { k: "Labels", v: selected.label / 100 }, { k: "Consumables", v: selected.consumable / 100 }]} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
                      <CartesianGrid {...gridProps} />
                      <XAxis dataKey="k" {...axisProps} />
                      <YAxis {...axisProps} width={42} />
                      <Tooltip {...tooltipStyle} />
                      <Bar dataKey="v" fill={chartColors.primary} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <div className="flex gap-2">
                <Button variant="outline" asChild>
                  <Link to="/app/billing/customers/$customerId" params={{ customerId: selected.customer_id }}>Open rate card</Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/app/billing" search={{ invoice: undefined } as any}>View invoices</Link>
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}